#!/usr/bin/env node

import { runDeployment } from './deploy';
import { runScaffoldCheck } from './scaffold-check';
import { runVerifyBuild } from './verify-build';
import { runSeedPhase5 } from './seed-phase5';
import { colors } from './const';
import { getErrorMessage } from '../types/shared';

const c = colors;
const args = process.argv.slice(2);
const command = args[0];

function printHelp(): void {
  console.log(`\n${c.bold}${c.cyan}CFM SDK CLI Tool${c.reset}`);
  console.log(`\nUsage:`);
  console.log(`  npx cfm-sdk scaffold-check              – Phase 1 framework gate`);
  console.log(`  npx cfm-sdk seed-phase5                 – Scaffold + install deps + implementation_plan (no bundle copy)`);
  console.log(`  npx cfm-sdk verify-build                – Phase 6 core verify (includes npm run build)`);
  console.log(`  npx cfm-sdk verify-build --fast         – Phase 5 speed check (required before INPUT 2)`);
  console.log(`  npx cfm-sdk sync-from-fetch             – INPUT 2: plan from live fetch`);
  console.log(`  npx cfm-sdk fill-implementation-plan --from-fetch – Auto-fill implementation_plan.md tables`);
  console.log(`  npx cfm-sdk get-deploy-target             – Read deploy.target from survey-deploy-config.json`);
  console.log(`  npx cfm-sdk set-deploy-target aws|vercel  – Write deploy.target after client confirms deploy`);
  console.log(`  npx cfm-sdk deploy                      – Deploy to Vercel`);
  console.log(`  npx cfm-sdk create-distribution --s3-key=<key>`);
  console.log(`                                          – Upload OUT_DIR to distribution path; syncs https://distribution-dashboard-three.vercel.app`);
  console.log(`  npx cfm-sdk doc-read-audit              – Audit fast-path doc read line budgets`);
  console.log(`  npx cfm-sdk doc-read-audit --profile phase --phase-id 1`);
}

async function main() {
  if (!command || command === '--help' || command === '-h') {
    printHelp();
    process.exit(0);
  }

  if (command === 'scaffold-check') {
    process.exit(runScaffoldCheck({}));
  } else if (command === 'seed-phase5') {
    process.exit(runSeedPhase5({}));
  } else if (command === 'sync-from-fetch') {
    const instanceArg = args.find((a) => a.startsWith('--instance-id='));
    const instanceIdOverride = instanceArg?.split('=').slice(1).join('=');
    const { runSyncFromFetch } = await import('./sync-from-fetch');
    process.exit(await runSyncFromFetch({ instanceIdOverride }));
  } else if (command === 'verify-build') {
    const fast = args.includes('--fast');
    process.exit(runVerifyBuild({ fast }));
  } else if (command === 'fill-implementation-plan') {
    const fromFetch = args.includes('--from-fetch');
    const instanceArg = args.find((a) => a.startsWith('--instance-id='));
    const instanceIdOverride = instanceArg?.split('=').slice(1).join('=');
    if (!fromFetch) {
      console.error(`${c.red}❌ Use: npx cfm-sdk fill-implementation-plan --from-fetch${c.reset}`);
      process.exit(1);
    }
    try {
      const { fillImplementationPlanFromFetch } = await import('./fill-implementation-plan');
      await fillImplementationPlanFromFetch({ instanceIdOverride });
    } catch (err: unknown) {
      console.error(`\n${c.red}❌ Error: ${getErrorMessage(err)}${c.reset}`);
      console.error(`${c.dim}Fallback: fill §3b–§3e tables manually in implementation_plan.md${c.reset}`);
      process.exit(1);
    }
  } else if (command === 'get-deploy-target') {
    try {
      const { runGetDeployTarget } = await import('./resolve-deploy-target');
      process.exit(runGetDeployTarget({}));
    } catch (err: unknown) {
      console.error(`\n${c.red}❌ Error: ${getErrorMessage(err)}${c.reset}`);
      process.exit(1);
    }
  } else if (command === 'set-deploy-target') {
    const target = args[1];
    try {
      const { runSetDeployTarget } = await import('./resolve-deploy-target');
      process.exit(await runSetDeployTarget({ target }));
    } catch (err: unknown) {
      console.error(`\n${c.red}❌ Error: ${getErrorMessage(err)}${c.reset}`);
      process.exit(1);
    }
  } else if (command === 'deploy') {
    try {
      await runDeployment();
    } catch (err: unknown) {
      console.error(`\n${c.red}❌ Error: ${getErrorMessage(err)}${c.reset}`);
      process.exit(1);
    }
  } else if (command === 'create-distribution') {
    const instanceArg = args.find((a) => a.startsWith('--instance-id='));
    const s3KeyArg = args.find((a) => a.startsWith('--s3-key='));
    const { runCreateDistribution } = await import('./create-distribution');
    process.exit(
      await runCreateDistribution({
        instanceIdOverride: instanceArg?.split('=').slice(1).join('='),
        s3Key: s3KeyArg?.split('=').slice(1).join('='),
      }),
    );
  } else if (command === 'doc-read-audit') {
    const { runDocReadAudit } = await import('./doc-read-audit');
    const profileArg = args.find((a) => a.startsWith('--profile='));
    const phaseIdArg = args.find((a) => a.startsWith('--phase-id='));
    const profileFlag = args.indexOf('--profile');
    const phaseIdFlag = args.indexOf('--phase-id');
    let profile: 'fastPath' | 'phase' = 'fastPath';
    let phaseId: string | undefined;
    if (profileArg) {
      const v = profileArg.split('=')[1];
      if (v === 'phase' || v === 'fastPath') profile = v;
    } else if (profileFlag >= 0) {
      const v = args[profileFlag + 1];
      if (v === 'phase' || v === 'fastPath') profile = v;
    }
    if (phaseIdArg) {
      phaseId = phaseIdArg.split('=')[1];
    } else if (phaseIdFlag >= 0) {
      phaseId = args[phaseIdFlag + 1];
    }
    process.exit(
      runDocReadAudit({
        profile,
        phaseId,
        warnOnly: args.includes('--warn-only'),
        ci: args.includes('--ci'),
        writeInventory: args.includes('--write'),
      }),
    );
  } else {
    console.error(`${c.red}Unknown command: ${command}${c.reset}\n`);
    printHelp();
    process.exit(1);
  }
}

main().catch((err: unknown) => {
  console.error(`${c.red}❌ Fatal error: ${getErrorMessage(err)}${c.reset}`);
  process.exit(1);
});
